import type { DutyAnalyticsDto } from '@pams/shared';
import { ReportSection, type SectionState } from '@/components/ReportSection';
import {
  TOTAL_CELL,
  ZERO_ATTENDANCE,
  cellFor,
  joinFigureRows,
  rowKeyOf,
  sectionData,
  type DutyAnalyticsWindowParam,
  type FigureRow,
  type GroupedFigures,
} from '@/lib/dutyAnalytics';
import { DUTY_ANALYTICS_COPY } from '../copy';
import { DELEGATION_INFO, MARKINGS_INFO, tableInfo } from '../figureInfo';
import { DelegationShape } from './DelegationShape';
import { FigureInfo } from './FigureInfo';
import { FiguresNotes } from './FiguresNotes';
import { FiguresTable, type FiguresTableRow, type FooterRow } from './FiguresTable';
import { HeadlineCards } from './HeadlineCards';
import { MarkingsBand } from './MarkingsBand';
import { SectionFailure } from './SectionFailure';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [P7C §2c] A DIVISION'S PAGE — its units, its own kept work, its total, and beside it the
 *  commissionerate's (U2).
 *
 *  ⚠️ U2 — "ALL DIVISIONS" IS A `plain` FOOTER ROW, so it carries no link BY TYPE, and the sentence
 *  under the table says why. It is a deliberate, figures-only disclosure the server computes from one
 *  global read; no drill parameter reaches it, and this page never receives another division's row, id,
 *  name or unit. The cost was accepted knowingly: with few divisions, subtraction reveals the rest.
 *
 *  ⚠️ NO DIVISION PICKER, AND `?division=` IS NEVER READ HERE — the server answers any `divisionId` on
 *  this layout with a 404, and the page only ever sends one once the server has echoed the
 *  commissionerate's layout.
 *
 *  ⚠️ DELEGATION IS ONE BAR, FROM THE TOTAL. A unit row can show more handed down than it was asked,
 *  because handed-down positions sit on the units' own shares; the division's total cannot.
 *
 *  ⚠️ [amended P7C.1 §4] THE SAME LAYOUT, READ FROM THE COMMISSIONERATE. `asDivisionId` is set only on a
 *  division's page opened from the commissionerate's Divisions table; every count then drills AS that
 *  division, and `pageDivision` carries the commissionerate page's own narrowing for the way back. On a
 *  division nodal's own page both are absent and every link is what it was.
 *
 *  ⚠️ [amended P7C.1 §3] EACH CARD AND SECTION CARRIES ITS OWN INFO BUTTON, and the Units dialog carries the layout
 *  note only when the division is read from the commissionerate — the one reader who has the other page to
 *  compare it with.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

const copy = DUTY_ANALYTICS_COPY;
const PREFIX = 'duty-analytics';

const nameOf = (row: FigureRow): string => row.identity.orgUnit?.name ?? copy.unknownUnit;

export const DivisionLayout = ({
  report,
  range,
  asDivisionId,
  pageDivision,
}: {
  report: DutyAnalyticsDto;
  range: DutyAnalyticsWindowParam;
  /** [P7C.1 §4] The division this page is read AS, from the commissionerate. */
  asDivisionId?: string;
  /** [P7C.1 §4] The commissionerate page's own `?division=`, for the way back from a drill. */
  pageDivision?: string;
}) => {
  const fill = sectionData(report.fill);
  const delegation = sectionData(report.delegation);
  const attendance = sectionData(report.attendance);
  const divisionName = report.viewer?.name ?? copy.yourDivision;
  const totalLabel = copy.divisionTotal(divisionName);

  const unitRows = joinFigureRows(report.rows, {
    fill: fill === null ? null : fill.rows,
    delegation: delegation === null ? null : delegation.rows,
    attendance: attendance === null ? null : attendance.rows,
  });
  const totals: GroupedFigures = {
    fill: fill?.total ?? null,
    delegation: delegation?.total ?? null,
    attendance: attendance?.total ?? null,
  };
  const label = (row: FigureRow): string =>
    row.identity.isDivisionItself ? copy.divisionKeptRow(nameOf(row)) : nameOf(row);

  const table: FiguresTableRow[] = unitRows.map((row) => ({
    key: rowKeyOf(row.identity),
    orgUnitId: row.identity.orgUnitId,
    label: label(row),
    figures: row.figures,
    cell: cellFor('DIVISION', 'rows', row.identity),
  }));

  const footer: FooterRow[] = [
    { kind: 'linked', testId: 'duty-analytics-division-total', label: totalLabel, figures: totals, cell: TOTAL_CELL },
  ];
  if (report.commissionerate !== null) {
    footer.push({
      kind: 'plain',
      testId: 'duty-analytics-commissionerate-total',
      label: copy.allDivisions,
      figures: {
        fill: sectionData(report.commissionerate.fill),
        delegation: sectionData(report.commissionerate.delegation),
        attendance: sectionData(report.commissionerate.attendance),
      },
    });
  }

  const delegationState: SectionState<{ orgUnitId: string; name: string; asked: number; handedDown: number }[]> =
    fill !== null && delegation !== null
      ? {
          data: [
            {
              orgUnitId: report.viewer?.id ?? 'division',
              name: divisionName,
              asked: fill.total.asked,
              handedDown: delegation.total.delegated,
            },
          ],
        }
      : { failure: 'UNAVAILABLE' };

  return (
    <>
      <HeadlineCards
        cards={fill?.cards ?? null}
        layout="DIVISION"
        range={range}
        pageDivision={pageDivision}
        asDivisionId={asDivisionId}
        scopeLabel={totalLabel}
      />

      <ReportSection
        id="units"
        testIdPrefix={PREFIX}
        title={copy.divisionUnitsTitle(divisionName)}
        state={report.fill}
        loadingLabel={copy.loadingSection(copy.unitsTitle)}
        failure={<SectionFailure />}
        aside={
          <FigureInfo
            infoKey="UNITS"
            title={copy.divisionUnitsTitle(divisionName)}
            content={tableInfo(asDivisionId !== undefined)}
          />
        }
      >
        {() => (
          <>
            <FiguresTable
              testId="duty-analytics-units"
              labelledBy={`${PREFIX}-units`}
              nameHeader={copy.unitHeader}
              rows={table}
              footer={footer}
              range={range}
              pageDivision={pageDivision}
              asDivisionId={asDivisionId}
              empty={copy.noUnits}
            />
            {report.commissionerate !== null && table.length > 0 && (
              <p className="text-xs text-muted-foreground" data-testid="duty-analytics-commissionerate-reason">
                {copy.commissionerateReason}
              </p>
            )}
          </>
        )}
      </ReportSection>

      <ReportSection
        id="delegation"
        testIdPrefix={PREFIX}
        title={copy.delegationTitle}
        state={delegationState}
        loadingLabel={copy.loadingSection(copy.delegationTitle)}
        failure={<SectionFailure />}
        aside={<FigureInfo infoKey="DELEGATION" title={copy.delegationTitle} content={DELEGATION_INFO} />}
      >
        {(rows) => <DelegationShape nameHeader={copy.divisionHeader} rows={rows} />}
      </ReportSection>

      <ReportSection
        id="markings"
        testIdPrefix={PREFIX}
        title={copy.markingsTitle}
        state={report.attendance}
        loadingLabel={copy.loadingSection(copy.markingsTitle)}
        failure={<SectionFailure />}
        aside={<FigureInfo infoKey="MARKINGS" title={copy.markingsTitle} content={MARKINGS_INFO} />}
      >
        {(section) => (
          <MarkingsBand
            nameHeader={copy.unitHeader}
            range={range}
            pageDivision={pageDivision}
            asDivisionId={asDivisionId}
            rows={unitRows.map((row) => ({
              key: rowKeyOf(row.identity),
              orgUnitId: row.identity.orgUnitId,
              label: label(row),
              figures: row.figures.attendance ?? ZERO_ATTENDANCE,
              cell: cellFor('DIVISION', 'rows', row.identity),
            }))}
            footer={{
              key: 'total',
              orgUnitId: null,
              label: totalLabel,
              figures: section.total,
              cell: TOTAL_CELL,
              testId: 'duty-analytics-markings-total',
            }}
          />
        )}
      </ReportSection>

      <FiguresNotes layout="DIVISION" />
    </>
  );
};
